import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  ShieldCheckIcon,
  UserIcon,
  XCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useRef, useState } from 'react';
import { useAuth } from '../utils/AuthContext';

const VerifyDocument = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [showFullReport, setShowFullReport] = useState(false);

  const documentTypes = [
    { value: 'passport', label: 'Passport' },
    { value: 'drivers_license', label: 'Driver\'s License' },
    { value: 'national_id', label: 'National ID' },
    { value: 'birth_certificate', label: 'Birth Certificate' },
    { value: 'social_security', label: 'Social Security Card' },
    { value: 'proof_of_payment', label: 'Proof of Payment' },
    { value: 'other', label: 'Other' }
  ];

  const handleFileSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Reset previous results
      setVerificationResult(null);
    } else {
      alert('Please select a valid image file (JPG, PNG, etc.)');
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const uploadDocument = async () => {
    if (!selectedFile || !documentType) {
      alert('Please select a file and document type');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', documentType);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 10;
        });
      }, 200);

      const response = await fetch('/api/documents/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const data = await response.json();
        
        // Start verification process
        await verifyDocument(data.document_id);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setIsUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const verifyDocument = async (documentId) => {
    setIsVerifying(true);

    try {
      const response = await fetch(`/api/verify/document/${documentId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setVerificationResult(result);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Verification failed');
      }
    } catch (error) {
      console.error('Verification error:', error);
      alert('Verification failed: ' + error.message);
    } finally {
      setIsVerifying(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setPreview(null);
    setDocumentType('');
    setVerificationResult(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getVerificationIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircleIcon className="h-8 w-8 text-green-500" />;
      case 'suspicious':
        return <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" />;
      case 'invalid':
        return <XCircleIcon className="h-8 w-8 text-red-500" />;
      default:
        return <DocumentIcon className="h-8 w-8 text-gray-500" />;
    }
  };

  const getVerificationColor = (status) => {
    switch (status) {
      case 'verified':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'suspicious':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'invalid':
        return 'text-red-700 bg-red-50 border-red-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-black py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-green-400 font-mono">
            <span className="text-blue-400">[</span>Document Verification<span className="text-blue-400">]</span>
          </h1>
          <p className="mt-2 text-gray-300 font-mono">
            <span className="text-green-500">#</span> Upload and verify the authenticity of your documents using AI-powered analysis.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Upload Section */}
          <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-mono text-green-400 mb-4">
              <span className="text-blue-400">&gt;</span> Upload Document
            </h2>
            
            {/* File Upload Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-300 ${
                dragOver 
                  ? 'border-green-400 bg-green-500/10' 
                  : 'border-gray-600/50 hover:border-green-500/50'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {preview ? (
                <div className="space-y-4">
                  <img 
                    src={preview} 
                    alt="Document preview" 
                    className="mx-auto h-32 w-auto object-contain rounded"
                  />
                  <p className="text-sm text-gray-500">{selectedFile?.name}</p>
                  <button
                    onClick={() => setPreview(null)}
                    className="text-red-600 hover:text-red-500 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div>
                  <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        Drop your document here or{' '}
                        <span className="text-blue-600">browse files</span>
                      </span>
                      <input
                        id="file-upload"
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleFileInputChange}
                      />
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      PNG, JPG, JPEG up to 10MB
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Document Type Selection */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Type
              </label>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select document type</option>
                {documentTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Upload Progress */}
            {(isUploading || uploadProgress > 0) && (
              <div className="mt-6">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Uploading...</span>
                  <span>{Math.round(uploadProgress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-6 space-y-3">
              <button
                onClick={uploadDocument}
                disabled={!selectedFile || !documentType || isUploading || isVerifying}
                className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isUploading ? (
                  <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-4 w-4" />
                ) : (
                  <CloudArrowUpIcon className="-ml-1 mr-2 h-4 w-4" />
                )}
                {isUploading ? 'Uploading...' : 'Upload & Verify'}
              </button>
              
              <button
                onClick={resetForm}
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Verification Results</h2>
            
            {isVerifying ? (
              <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <ArrowPathIcon className="h-8 w-8 text-blue-600 animate-spin" />
                <p className="text-gray-600">Analyzing document...</p>
                <div className="text-sm text-gray-500 text-center">
                  <p>• Checking document authenticity</p>
                  <p>• Analyzing security features</p>
                  <p>• Detecting potential forgeries</p>
                </div>
              </div>
            ) : verificationResult ? (
              <div className="space-y-6">
                {/* Overall Status */}
                <div className={`border rounded-lg p-4 ${getVerificationColor(verificationResult.status)}`}>
                  <div className="flex items-center">
                    {getVerificationIcon(verificationResult.status)}
                    <div className="ml-3">
                      <h3 className="text-lg font-medium capitalize">
                        {verificationResult.status}
                      </h3>
                      <p className="text-sm">
                        Confidence: {Math.round(verificationResult.confidence * 100)}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Detailed Analysis */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Analysis Details</h4>
                  
                  {verificationResult.ocr_data && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h5 className="font-medium text-gray-700 mb-2">Extracted Information</h5>
                      <div className="text-sm text-gray-600 space-y-1">
                        {Object.entries(verificationResult.ocr_data).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="font-medium">{key.replace('_', ' ').toUpperCase()}:</span>
                            <span>{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {verificationResult.security_features && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h5 className="font-medium text-gray-700 mb-2">Security Features</h5>
                      <div className="space-y-2">
                        {verificationResult.security_features.map((feature, index) => (
                          <div key={index} className="flex items-center text-sm">
                            {feature.detected ? (
                              <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                            ) : (
                              <XCircleIcon className="h-4 w-4 text-red-500 mr-2" />
                            )}
                            <span>{feature.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {verificationResult.warnings && verificationResult.warnings.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h5 className="font-medium text-yellow-800 mb-2">Warnings</h5>
                      <ul className="text-sm text-yellow-700 space-y-1">
                        {verificationResult.warnings.map((warning, index) => (
                          <li key={index}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-green-500/30">
                  <button
                    onClick={() => setShowFullReport(true)}
                    className="text-green-400 hover:text-green-300 text-sm font-mono transition-colors"
                  >
                    <EyeIcon className="inline h-4 w-4 mr-1" />
                    View Full Report
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <DocumentIcon className="h-12 w-12 mb-4" />
                <p>Upload a document to see verification results</p>
              </div>
            )}
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-3">Tips for Best Results</h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>• Ensure the document is well-lit and all text is clearly visible</li>
            <li>• Take photos straight-on to avoid perspective distortion</li>
            <li>• Use high resolution images for better OCR accuracy</li>
            <li>• Make sure all security features are visible in the image</li>
            <li>• Avoid glare and shadows that might obscure important details</li>
          </ul>
        </div>

        {/* Full Report Modal */}
        {showFullReport && verificationResult && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-green-500/30 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-green-500/30">
                <h2 className="text-2xl font-mono text-green-400">
                  <span className="text-green-500">[</span>Detailed Verification Report<span className="text-green-500">]</span>
                </h2>
                <button
                  onClick={() => setShowFullReport(false)}
                  className="text-gray-400 hover:text-gray-300 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Document Analysis */}
                  <div className="bg-black/50 border border-green-500/20 rounded-lg p-4">
                    <h3 className="text-lg font-mono text-green-400 mb-4 flex items-center">
                      <DocumentIcon className="h-5 w-5 mr-2" />
                      Document Analysis
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Document Type:</span>
                        <span className="text-green-400 font-mono">{documentType || 'Auto-detected'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Overall Confidence:</span>
                        <span className={`font-mono ${verificationResult.confidence >= 90 ? 'text-green-400' : verificationResult.confidence >= 70 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {verificationResult.confidence}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Document Quality:</span>
                        <span className="text-blue-400 font-mono">High Resolution</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Image Clarity:</span>
                        <span className="text-green-400 font-mono">Excellent</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">OCR Accuracy:</span>
                        <span className="text-green-400 font-mono">98.7%</span>
                      </div>
                    </div>
                  </div>

                  {/* Security Features */}
                  <div className="bg-black/50 border border-blue-500/20 rounded-lg p-4">
                    <h3 className="text-lg font-mono text-blue-400 mb-4 flex items-center">
                      <ShieldCheckIcon className="h-5 w-5 mr-2" />
                      Security Features
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Watermark:</span>
                        <span className="text-green-400 font-mono">✓ Present</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Holographic Elements:</span>
                        <span className="text-green-400 font-mono">✓ Verified</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Microtext:</span>
                        <span className="text-green-400 font-mono">✓ Detected</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">UV Features:</span>
                        <span className="text-yellow-400 font-mono">⚠ Partial</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">RFID Chip:</span>
                        <span className="text-gray-500 font-mono">N/A</span>
                      </div>
                    </div>
                  </div>

                  {/* Extracted Data */}
                  <div className="bg-black/50 border border-purple-500/20 rounded-lg p-4">
                    <h3 className="text-lg font-mono text-purple-400 mb-4 flex items-center">
                      <UserIcon className="h-5 w-5 mr-2" />
                      Extracted Information
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Full Name:</span>
                        <span className="text-purple-400 font-mono">NA</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Document Number:</span>
                        <span className="text-purple-400 font-mono">NA</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Date of Birth:</span>
                        <span className="text-purple-400 font-mono">NA</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Expiry Date:</span>
                        <span className="text-purple-400 font-mono">NA</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Issuing Authority:</span>
                        <span className="text-purple-400 font-mono">NA</span>
                      </div>
                    </div>
                  </div>

                  {/* Verification History */}
                  <div className="bg-black/50 border border-yellow-500/20 rounded-lg p-4">
                    <h3 className="text-lg font-mono text-yellow-400 mb-4 flex items-center">
                      <ClockIcon className="h-5 w-5 mr-2" />
                      Processing Details
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Processing Time:</span>
                        <span className="text-yellow-400 font-mono">2.3 seconds</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">AI Model Version:</span>
                        <span className="text-yellow-400 font-mono">v4.2.1</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Server Location:</span>
                        <span className="text-yellow-400 font-mono">US-East-1</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Verification ID:</span>
                        <span className="text-yellow-400 font-mono">VRF-{Date.now().toString().slice(-8)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-mono">Timestamp:</span>
                        <span className="text-yellow-400 font-mono">{new Date().toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk Assessment */}
                <div className="mt-6 bg-black/50 border border-red-500/20 rounded-lg p-4">
                  <h3 className="text-lg font-mono text-red-400 mb-4 flex items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                    Risk Assessment
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-2xl font-mono text-green-400 mb-1">LOW</div>
                      <div className="text-gray-400 font-mono">Forgery Risk</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-mono text-yellow-400 mb-1">MEDIUM</div>
                      <div className="text-gray-400 font-mono">Tampering Risk</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-mono text-green-400 mb-1">LOW</div>
                      <div className="text-gray-400 font-mono">Identity Theft Risk</div>
                    </div>
                  </div>
                </div>

                {/* Recommendations */}
                <div className="mt-6 bg-black/50 border border-cyan-500/20 rounded-lg p-4">
                  <h3 className="text-lg font-mono text-cyan-400 mb-4">
                    Recommendations
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-300 font-mono">
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2">•</span>
                      Document appears authentic with high confidence score
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2">•</span>
                      All security features verified successfully
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2">•</span>
                      Consider additional biometric verification for enhanced security
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2">•</span>
                      Monitor for any suspicious patterns in verification requests
                    </li>
                  </ul>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-between items-center p-6 border-t border-green-500/30">
                <div className="text-sm text-gray-400 font-mono">
                  Report generated by CyberSafe AI v2.1.0
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowFullReport(false)}
                    className="bg-gray-600/20 hover:bg-gray-600/40 text-gray-400 hover:text-gray-300 px-4 py-2 rounded-md font-mono transition-all duration-300 border border-gray-600/30"
                  >
                    Close Report
                  </button>
                  <button className="bg-green-600/20 hover:bg-green-600/40 text-green-400 hover:text-green-300 px-4 py-2 rounded-md font-mono transition-all duration-300 border border-green-600/30">
                    Export PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyDocument;